
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface ContactInfo {
  phones: string[];
  email: string;
  addresses_ar: string[];
  addresses_en: string[];
  whatsapp: string;
  working_hours_ar: string;
  working_hours_en: string;
}

interface SocialLinks {
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
  whatsapp: string;
}

// بيانات فارغة - سيتم تحميلها من قاعدة البيانات
const defaultContact: ContactInfo = {
  phones: [],
  email: '',
  addresses_ar: [],
  addresses_en: [],
  whatsapp: '',
  working_hours_ar: '',
  working_hours_en: '',
};

const defaultSocial: SocialLinks = {
  facebook: '',
  twitter: '',
  instagram: '',
  linkedin: '',
  whatsapp: '',
};

export const useContactInfo = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>(defaultContact);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>(defaultSocial);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [contactRes, socialRes] = await Promise.all([
        supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'contact_info')
          .maybeSingle(),
        supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'social_links')
          .maybeSingle(),
      ]);

      // ---------- Contact Info ----------
      if (contactRes.data?.setting_value) {
        const fetchedContact =
          typeof contactRes.data.setting_value === 'object'
            ? contactRes.data.setting_value
            : {};

        // Convert old data format to the new ContactInfo shape
        const migratedContact: ContactInfo = { ...defaultContact };

        // phones (old key: phone)
        if (Array.isArray(fetchedContact.phones)) {
          migratedContact.phones = fetchedContact.phones;
        } else if (fetchedContact.phone) {
          migratedContact.phones = [fetchedContact.phone];
        }

        // addresses_ar (old key: address_ar)
        if (Array.isArray(fetchedContact.addresses_ar)) {
          migratedContact.addresses_ar = fetchedContact.addresses_ar;
        } else if (fetchedContact.address_ar) {
          migratedContact.addresses_ar = [fetchedContact.address_ar];
        }

        // addresses_en (old key: address_en)
        if (Array.isArray(fetchedContact.addresses_en)) {
          migratedContact.addresses_en = fetchedContact.addresses_en;
        } else if (fetchedContact.address_en) {
          migratedContact.addresses_en = [fetchedContact.address_en];
        }

        if (fetchedContact.email) migratedContact.email = fetchedContact.email;
        if (fetchedContact.whatsapp) migratedContact.whatsapp = fetchedContact.whatsapp;
        if (fetchedContact.working_hours_ar) migratedContact.working_hours_ar = fetchedContact.working_hours_ar;
        if (fetchedContact.working_hours_en) migratedContact.working_hours_en = fetchedContact.working_hours_en;

        setContactInfo(migratedContact);
      }

      // ---------- Social Links ----------
      if (socialRes.data?.setting_value) {
        const fetchedSocial =
          typeof socialRes.data.setting_value === 'object'
            ? socialRes.data.setting_value
            : {};

        setSocialLinks({ ...defaultSocial, ...fetchedSocial });
      }
    } catch (error) {
      // Provide a more descriptive error message while still logging the original error
      console.error('Failed to load contact information:', error);
    } finally {
      setLoading(false);
    }
  };

  return { contactInfo, socialLinks, loading };
};
